import type { ProPlanChangePreview, ProSubscriptionSummary } from '@/shared/domain'
import { formatLongDate } from '@/shared/infra/format'
import { cardLabel } from '@/shared/ui/layout'
import { formatCurrencyFromMinorUnits } from './format-currency-from-minor-units'

export const PRO_UPGRADE_PLAN_LABEL = 'BlindBook Pro + Waterfowl U'
export const PRO_UPGRADE_CREDIT_LABEL = 'Credit for BlindBook Pro'
export const PRO_UPGRADE_DUE_TODAY_LABEL = 'Due today'

export type ProUpgradeCharge = {
  planPriceLabel: string
  creditLabel: string
  amountDueNowLabel: string
  renewalAmountLabel: string
  renewalDateLabel: string
  cardLabel: string
  periodResetLine: string
  confirmDescription: string
  confirmDetails: string[]
}

export type SelectProUpgradeChargeInput = {
  preview: ProPlanChangePreview | null
  subscription: ProSubscriptionSummary | null
  isTrial: boolean
}

const EMPTY_CHARGE: ProUpgradeCharge = {
  planPriceLabel: '',
  creditLabel: '',
  amountDueNowLabel: '',
  renewalAmountLabel: '',
  renewalDateLabel: '',
  cardLabel: '',
  periodResetLine: '',
  confirmDescription: '',
  confirmDetails: [],
}

export function selectProUpgradeCharge(input: SelectProUpgradeChargeInput): ProUpgradeCharge {
  const { preview, subscription, isTrial } = input
  if (preview === null) {
    return {
      ...EMPTY_CHARGE,
      renewalDateLabel: formatLongDate(subscription?.currentPeriodEnd),
    }
  }

  const planPriceLabel =
    preview.nextRenewalAmount === null
      ? ''
      : formatCurrencyFromMinorUnits(preview.nextRenewalAmount, preview.currency)
  const renewalAmountLabel = planPriceLabel === '' ? '' : `${planPriceLabel}/yr`
  const renewalDateLabel = formatLongDate(preview.nextRenewalAt ?? subscription?.currentPeriodEnd)
  const amountDueNowLabel = formatCurrencyFromMinorUnits(preview.amountDueNow, preview.currency)
  const creditLabel =
    preview.creditAmount > 0
      ? `−${formatCurrencyFromMinorUnits(preview.creditAmount, preview.currency)}`
      : ''
  const chargedToLabel =
    subscription?.defaultCardLast4 == null
      ? ''
      : `Charged to ${cardLabel(subscription.defaultCardBrand ?? undefined, subscription.defaultCardLast4)}`
  const periodResetLine =
    isTrial || renewalDateLabel === ''
      ? ''
      : `Your billing year restarts today — your next renewal moves to ${renewalDateLabel}.`

  const confirmDetails: string[] = []
  if (planPriceLabel !== '') {
    confirmDetails.push(`${PRO_UPGRADE_PLAN_LABEL} — ${planPriceLabel}`)
  }
  if (creditLabel !== '') {
    confirmDetails.push(`${PRO_UPGRADE_CREDIT_LABEL} — ${creditLabel}`)
  }
  confirmDetails.push(
    isTrial ? 'Nothing to pay today' : `${PRO_UPGRADE_DUE_TODAY_LABEL} — ${amountDueNowLabel}`,
  )
  if (chargedToLabel !== '') {
    confirmDetails.push(chargedToLabel)
  }
  if (renewalAmountLabel !== '') {
    confirmDetails.push(
      renewalDateLabel === ''
        ? `Then ${renewalAmountLabel}.`
        : `Then ${renewalAmountLabel} on ${renewalDateLabel}.`,
    )
  }
  if (periodResetLine !== '') {
    confirmDetails.push(periodResetLine)
  }

  return {
    planPriceLabel,
    creditLabel,
    amountDueNowLabel,
    renewalAmountLabel,
    renewalDateLabel,
    cardLabel: chargedToLabel,
    periodResetLine,
    confirmDescription: isTrial
      ? 'Nothing is charged today. Waterfowl U is added to your subscription now.'
      : `${amountDueNowLabel} will be charged now.`,
    confirmDetails,
  }
}
